import { process, State } from '@progress/kendo-data-query';
import { GridDataStateChangeEvent } from '@progress/kendo-react-grid';
import { PAGING_OPTIONS } from 'common/constants';
import { searchInObject } from 'common/hooks/utils';
import { WidgetModel } from 'common/models/widget.model';
import { RootState } from 'common/store';
import { ListResponse } from 'common/store/api/chart.api';
import { selectPagination, setPagination } from 'common/store/slices/pagination.slice';
import { selectWidgetSearchQuery } from 'common/store/slices/search.slice';
import {
  selectAllWidgetDataById,
  setFilteredWidgetData,
} from 'common/store/slices/widget-data.slice';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface SearchFilter {
  searchColumns?: string[];
  widget: WidgetModel;
  data: ListResponse;
}

export const useWidgetSearchFilter = ({ searchColumns, widget, data }: SearchFilter) => {
  const dispatch = useDispatch();
  const allWidgetData = useSelector((state: RootState) =>
    selectAllWidgetDataById(state, widget.id)
  );
  const pagination = useSelector((state: RootState) => selectPagination(state, widget.id));

  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [dataState, setDataState] = useState<State>(data as State);
  const [dataResult, setDataResult] = useState(process(data.data, dataState));
  const filterValue = useSelector((state: RootState) => selectWidgetSearchQuery(state, widget.id));

  const onFilterChange = useCallback(
    (value: string) => {
      const filterRow = (item: any) => {
        if (!item.items) {
          const result = searchInObject(item, value, searchColumns);
          return result;
        }
        const foundRows = item?.items.filter(filterRow);
        return foundRows.length > 0;
      };

      const mapRowItems = (row: any): unknown => {
        const transformedRow = {
          ...row,
        };
        if (!transformedRow.items && !transformedRow.subgroups) {
          return transformedRow;
        }
        if (row.subgroups?.length > 0) {
          transformedRow.items = row.items
            .map(mapRowItems)
            .filter((itemRow: any) => itemRow.items.length > 0);
        } else {
          transformedRow.items = transformedRow.items.filter(filterRow);
        }
        return transformedRow;
      };
      const newData =
        value.length > 0
          ? allWidgetData?.data?.map(mapRowItems).filter(filterRow) ?? []
          : data.data;

      const skipValue = pagination && newData.length < pagination?.skip ? 0 : pagination?.skip ?? 0;

      const widgetData = widget.request?.data as any;
      const paginatedData = pagination
        ? newData?.slice(skipValue, skipValue + pagination.take)
        : newData?.slice(0, widgetData?.pageSize ?? PAGING_OPTIONS[0]);

      const updatedData = value.length > 0 ? paginatedData ?? [] : newData ?? [];
      const totalCount = value.length > 0 ? newData?.length ?? updatedData?.length : data.total;
      if (!value) {
        dispatch(
          setFilteredWidgetData({
            id: widget.id,
            data: allWidgetData ?? { data: newData, total: totalCount },
          })
        );
      } else {
        dispatch(
          setFilteredWidgetData({
            id: widget.id,
            data: { data: newData, total: totalCount },
          })
        );
      }

      if (JSON.stringify(filteredData) !== JSON.stringify(updatedData)) {
        if (pagination?.total !== totalCount) {
          dispatch(
            setPagination({
              id: widget.id,
              skip: skipValue,
              take: pagination?.take ?? 20,
              total: totalCount,
            })
          );
        }

        setFilteredData(updatedData);
        setDataState((state) => {
          const clearedPagerDataState = { ...state };
          const processedData = process(updatedData, clearedPagerDataState);
          setDataResult(processedData);
          return clearedPagerDataState;
        });
      }
    },
    [
      data,
      searchColumns,
      allWidgetData,
      pagination,
      filteredData,
      dispatch,
      widget.id,
      widget.request?.data,
    ]
  );

  useEffect(() => {
    onFilterChange(filterValue);
  }, [filterValue, dispatch, onFilterChange]);

  const dataStateChangeWithTags = useCallback(
    (event: any) => {
      const data = filteredData?.map((item: Record<string, unknown>) => ({
        ...(item ?? {}),
        tagsCounter: Object.keys(item?.tags ?? {}).length,
      }));
      setDataResult(process(data, event.dataState));
      setDataState(event.dataState);
    },
    [filteredData]
  );

  const dataStateChange = useCallback(
    (event: GridDataStateChangeEvent) => {
      setDataResult(process(filteredData, event.dataState));
      setDataState(event.dataState);
    },
    [filteredData]
  );

  return {
    onFilterChange,
    dataStateChangeWithTags,
    dataStateChange,
    filteredData,
    dataResult,
    dataState,
    totalCount: pagination?.total ?? data.total,
  };
};
